/**
 * /ko/product-discovery — 프로덕트 디스커버리 concept page (Korean).
 * Links /ko/voice-to-text + /ko/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function KoProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/product-discovery"
      hreflangLocales={['ko']}
      documentTitle="프로덕트 디스커버리란? 방법과 진행 과정 · Draftlize"
      documentDescription="프로덕트 디스커버리의 방법과 과정을 정리하고, 사용자 인터뷰에서 얻은 발견을 추적 가능한 제품 의사결정으로 바꾸는 법을 설명합니다."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 디스커버리</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 54px)', lineHeight: 1.1, margin: '12px 0 24px' }}>
            프로덕트 디스커버리——<br />
            <em>발견을 결정으로.</em>
          </h1>
          <p className="lede">
            프로덕트 디스커버리는 만들기 전에 "무엇을 만들어야 하는가"를 확인하는 활동입니다. 사용자 인터뷰·관찰·검증으로 가정을 사실로 바꿉니다. 잘하는 팀도 약점은 같은 곳에 있습니다——어렵게 얻은 <em>인사이트가 결정으로 이어지기 전에 사라집니다</em>. 이 페이지는 디스커버리의 방법과, 발견을 "추적 가능한 결정"으로 바꾸는 법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">디스커버리의 방법.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>사용자 인터뷰</strong>——답이 아니라 문제와 맥락을 이야기로 듣습니다.</li>
            <li><strong>관찰·행동 데이터</strong>——말이 아니라 행동을 봅니다.</li>
            <li><strong>검증(프로토타입·실험)</strong>——작게 만들어 가설을 시험합니다.</li>
            <li><strong>통합</strong>——흩어진 발견을 결정에 충분한 패턴으로 모읍니다.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            인터뷰는 <a href="/ko/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>음성 텍스트 변환</a>으로 재료화하는 것부터. 다만 전사본은 끝이 아니라, 통합과 결정의 출발점입니다.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">약점은 "발견→결정" 사이. <em>여기를 잇는다.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            디스커버리의 성과는 인터뷰의 발언 자체가 아니라, 그로부터 내린 <em>결정</em>입니다. 많은 팀이 발견을 슬라이드로 정리하고 끝내, 결정과 이유가 어디에도 남지 않습니다. Draftlize는 "발언 → 결정 → 요구사항"을 한 줄로 잇습니다.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">왜 인사이트가 사라지나</h3>
            <p>인터뷰 하이라이트가 Notion 페이지나 회의록에 흩어지고, 다음 스프린트엔 아무도 다시 보지 않습니다. 결정의 "왜"가 발견과 분리되기 때문입니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">"발언 → 결정 → 요구사항"을 잇는다</h3>
            <p>Draftlize에서는 사용자의 한마디를 그것이 이끈 결정, 그리고 거기서 나온 요구사항에 연결합니다. 이후 검증이 전제를 뒤집으면 의존하는 것이 자동 stale.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 에이전트가 발견을 반영한다</h3>
            <p>디스커버리의 결정이 MCP로 Claude Code·Cursor가 읽는 컨텍스트가 됩니다. 다음 스펙은 "사용자가 이렇게 말해서 이렇게 정했다"를 반영해 쓸 수 있습니다. 이어서 <a href="/ko/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>의사결정 기록</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          디스커버리의 가치는 모은 인사이트의 양이 아니다. 반년 뒤에도 그로부터 내린 결정을 되짚을 수 있는가다.
        </blockquote>
        <cite>발견을 결정에 잇고, 전제가 바뀌면 알린다. 그게 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '디스커버리와 딜리버리의 차이는?', a: '디스커버리는 "옳은 것을 만드는가"를 확인하고, 딜리버리는 "옳게 만든다"입니다. 둘은 함께 가지만, 디스커버리의 결정이 남지 않으면 딜리버리가 방향을 놓칩니다.' },
            { q: '인터뷰는 몇 명?', a: '정확한 수보다, 새 발견이 더 나오지 않을(포화) 때까지가 기준입니다. 중요한 건 인원수가 아니라 각 발언을 어떤 결정에 연결했는지입니다.' },
            { q: '발견을 어떻게 통합하나요?', a: '발언을 주제별로 모아 결정에 충분한 패턴을 찾습니다. Draftlize에서는 각 패턴을 결정 카드로 만들어 원래 발언에 연결할 수 있습니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">인터뷰의 한마디를 결정으로 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            디스커버리의 발견을 Draftlize에서 "발언 → 결정 → 요구사항" 카드로. 전제가 바뀌면 자동 stale, AI 에이전트도 읽습니다.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
